import { useState, type FormEvent } from 'react'
import { BrandMark } from '../../components/BrandMark'
import { ShareApiError, verify } from '../../lib/shareApi'
import './GateScreen.css'

interface GateScreenProps {
  token: string
  onVerified: (viewToken: string) => void
}

/** Friendly, wellness-framed copy per failure reason (no clinical alarm). */
function messageFor(reason: ShareApiError['reason']): string {
  switch (reason) {
    case 'invalid_code':
      return "That code doesn't match. Check it and try again."
    case 'locked':
      return 'Too many attempts. Wait a few minutes, then try again.'
    case 'expired':
      return 'This link has expired. Ask for a fresh link and code.'
    case 'not_found':
      return 'This link is no longer active. Ask for a fresh link and code.'
    case 'network':
      return 'Could not reach Stella. Check your connection and try again.'
    default:
      return 'Something went wrong. Please try again.'
  }
}

export function GateScreen({ token, onVerified }: GateScreenProps) {
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [focused, setFocused] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const ready = code.length === 6 && !busy
  const slots = [0, 1, 2, 3, 4, 5]

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!ready) return
    setBusy(true)
    setError(null)
    try {
      const { viewToken } = await verify(token, code)
      onVerified(viewToken)
    } catch (err) {
      const reason = err instanceof ShareApiError ? err.reason : 'unknown'
      setError(messageFor(reason))
      setBusy(false)
    }
  }

  return (
    <main className='gate'>
      <form className='gate__card' onSubmit={handleSubmit} noValidate>
        <BrandMark />
        <h1 className='gate__title'>A skin profile, shared with you</h1>
        <p className='gate__sub'>
          Someone shared their Stella skin profile with you. Enter the 6-digit
          access code to open this live view. It shows measurements only, and
          access can be turned off at any time.
        </p>

        <label className='gate__label' htmlFor='code'>
          Access code
        </label>
        {/* Six visible slots over one real input, so autofill / paste / the
            OS one-time-code affordance keep working while the field reads as a
            scannable 6-digit code. */}
        <div className='gate__code' data-filled={code.length > 0}>
          {slots.map((i) => {
            const active =
              focused && (i === code.length || (code.length === 6 && i === 5))
            return (
              <div
                key={i}
                className={`gate__slot${active ? ' gate__slot--active' : ''}`}
                aria-hidden='true'
              >
                {code[i] ?? ''}
              </div>
            )
          })}
          <input
            id='code'
            className='gate__code-input'
            inputMode='numeric'
            autoComplete='one-time-code'
            pattern='[0-9]*'
            maxLength={6}
            aria-label='6-digit access code'
            aria-describedby={error ? 'gate-error' : undefined}
            value={code}
            autoFocus
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onChange={(e) => {
              setError(null)
              setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
            }}
          />
        </div>

        {error && (
          <p id='gate-error' className='gate__error' role='alert'>
            {error}
          </p>
        )}

        <button className='gate__submit' type='submit' disabled={!ready}>
          {busy ? 'Opening…' : 'View profile'}
        </button>

        <p className='gate__foot'>
          Stella measures the visible appearance of skin from a phone photo. It
          is a wellness tracking tool, not a diagnosis.
        </p>
      </form>
    </main>
  )
}
